/**
 * Facilities, Entrega 3c-3 (ADR 0036): PMOC (Plano de Manutenção, Operação e Controle dos sistemas
 * de climatização) por unidade ou prédio. O sistema nunca indica "conforme": mostra só o que falta
 * ou venceu; a validação é registrada a partir do responsável técnico.
 */
import { z } from 'zod';

/** Rascunho até a validação; qualquer mudança no escopo ou no responsável volta para rascunho. */
export const PMOC_STATUSES = ['draft', 'validated', 'closed'] as const;
export type PmocStatus = (typeof PMOC_STATUSES)[number];

/** Responsável técnico: pessoa da equipe de manutenção ou prestador do cadastro de Parceiros. */
export const PMOC_TECHNICIAN_KINDS = ['internal', 'provider'] as const;
export type PmocTechnicianKind = (typeof PMOC_TECHNICIAN_KINDS)[number];

/** ART (ou equivalente), plano assinado pelo responsável técnico, laudos e relatórios, outros. */
export const PMOC_DOCUMENT_CATEGORIES = ['art', 'signed_plan', 'report', 'other'] as const;
export type PmocDocumentCategory = (typeof PMOC_DOCUMENT_CATEGORIES)[number];

export const PMOC_EVENT_KINDS = [
  'created',
  'updated',
  'asset_added',
  'asset_removed',
  'plan_added',
  'plan_removed',
  'document_added',
  'document_replaced',
  'document_removed',
  'validated',
  'validation_reset',
  'closed',
] as const;

/** Situação de um documento na data: sem validade, vigente ou vencido. */
export type PmocDocumentState = 'no_validity' | 'valid' | 'expired';

export function pmocDocumentState(validUntil: string | null, today: string): PmocDocumentState {
  if (!validUntil) return 'no_validity';
  return validUntil < today ? 'expired' : 'valid';
}

/** Pendência que impede registrar a validação (nunca um "conforme"). */
export type PmocPendency = 'no_assets' | 'art_missing' | 'art_expired' | 'signed_plan_missing';

/**
 * O que falta para registrar a validação: equipamento, ART vigente e plano assinado depois da
 * última mudança de escopo. O banco confere de novo.
 */
export function pmocPendencies(input: {
  assetCount: number;
  documents: { category: string; validUntil: string | null; createdAt: string }[];
  scopeChangedAt: string;
  today: string;
}): PmocPendency[] {
  const out: PmocPendency[] = [];
  if (input.assetCount === 0) out.push('no_assets');
  const arts = input.documents.filter((d) => d.category === 'art');
  if (arts.length === 0) out.push('art_missing');
  else if (!arts.some((d) => pmocDocumentState(d.validUntil, input.today) === 'valid')) {
    out.push('art_expired');
  }
  const scope = Date.parse(input.scopeChangedAt);
  if (
    !input.documents.some((d) => d.category === 'signed_plan' && Date.parse(d.createdAt) >= scope)
  ) {
    out.push('signed_plan_missing');
  }
  return out;
}

const optional = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const optionalId = z
  .uuid()
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();

/** PMOC: unidade (e prédio, se não for a unidade inteira) e responsável técnico. */
export const pmocInput = z
  .object({
    legalEntityId: z.uuid(),
    branchId: z.uuid(),
    locationId: optionalId,
    title: z.string().trim().min(1).max(200),
    notes: optional(4000),
    technicianKind: z.enum(PMOC_TECHNICIAN_KINDS),
    technicianUserId: optionalId,
    technicianPartnerId: optionalId,
    technicianName: z.string().trim().min(1).max(150),
    technicianRegistration: z.string().trim().min(1).max(60),
  })
  .refine((v) => v.technicianKind !== 'internal' || !!v.technicianUserId, {
    path: ['technicianUserId'],
    message: 'Escolha a pessoa responsável técnica',
  })
  .refine((v) => v.technicianKind !== 'provider' || !!v.technicianPartnerId, {
    path: ['technicianPartnerId'],
    message: 'Escolha o prestador no cadastro de parceiros',
  });
export type PmocInput = z.infer<typeof pmocInput>;

export const pmocAssetInput = z.object({ assetId: z.uuid() });
export const pmocPlanInput = z.object({ maintenancePlanId: z.uuid() });
export const pmocValidateInput = z.object({ note: optional(2000) });
export const pmocReasonInput = z.object({ reason: z.string().trim().min(5).max(2000) });
